import React, { useState } from "react";
import { createDate } from "../utils/notificationDate";
import { format } from "date-fns";
import useFetch from "../../../../hooks/useFetch";
import LabeledSelect from "../../../../components/Select/LabeledSelect";

const PushNotificationForm = ({
  initialData = {},
  onSubmit,
  onCancel,
  isNew = true,
}) => {
  const [form, setForm] = useState(() => ({
    title: initialData.title || "",
    message: initialData.message || "",
    entityType: initialData.entityType || "APP_UPDATE",
    entityId: initialData.entityId === -1 ? undefined : initialData.entityId,
    countryCode: initialData.countryCode || undefined,
    link: initialData.link || undefined,
    expiresAt: initialData.expiresAt || createDate(5),
  }));

  const [errors, setErrors] = useState({});

  const { data: entityTypes, loading: typesLoading } = useFetch(
    `/v1/admin/notifications/entityTypes`,
  );

  const { data: countries, loading: countriesLoading } =
    useFetch(`/v2/countries`);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "entityType") {
      setForm((prev) => ({ ...prev, [name]: value, entityId: "", link: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = {};

    if (!form.title.trim()) {
      validationErrors.title = "Invalid title";
    }

    if (!form.message.trim()) {
      validationErrors.message = "Invalid message";
    }

    if (!form.entityType.trim()) {
      validationErrors.entityType = "Invalid entity type";
    }

    if ((form.entityId !== null || form.entityId !== "") && form.entityId < 0) {
      validationErrors.entityId = "Invalid entity id";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit({ ...form });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 max-w-xl mx-auto"
    >
      <div className="flex flex-col gap-5 w-full items-center">
        <div className="w-full">
          <label className="block text-xs mb-1 font-semibold">Title</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className={`w-full border p-2 text-xs ${errors.title && "border-red-500"}`}
          />
          {errors.title && (
            <div className="text-red-600 text-xs">{errors.title}</div>
          )}
        </div>
        <div className="w-full">
          <label className="block text-xs mb-1 font-semibold">Message</label>
          <input
            type="text"
            name="message"
            value={form.message}
            onChange={handleChange}
            className={`w-full border p-2 text-xs ${errors.message && "border-red-500"}`}
          />
          {errors.message && (
            <div className="text-red-600 text-xs">{errors.message}</div>
          )}
        </div>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {typesLoading ? (
            <span>Loading...</span>
          ) : (
            <LabeledSelect
              label="Entity type"
              name="entityType"
              labelClassName={"block text-xs mb-1 font-semibold"}
              onChange={handleChange}
              value={form.entityType}
              options={entityTypes.map((type) => ({
                value: type,
                label: type,
              }))}
            />
          )}
          {countriesLoading ? (
            <span>Loading...</span>
          ) : (
            <LabeledSelect
              label="Country"
              name="countryCode"
              onChange={handleChange}
              labelClassName={"block text-xs mb-1 font-semibold"}
              value={form.countryCode}
              defaultOption={{ value: null, label: "Not selected" }}
              disabled={!isNew}
              options={countries.map((country) => ({
                value: country.code,
                label: country.name,
              }))}
            />
          )}
          <div className="relative">
            <label className="block text-xs mb-1 font-semibold">
              Entity id
            </label>
            <input
              type="number"
              name="entityId"
              value={form.entityId}
              onChange={handleChange}
              required={
                form.entityType !== "APP_UPDATE" || form.entityType !== "LINK"
              }
              disabled={
                form.entityType === "APP_UPDATE" || form.entityType === "LINK"
              }
              min={1}
              className="custom-select"
              placeholder="Entity id"
            />
            {errors.entityId && (
              <div className="text-red-600 text-xs">{errors.entityId}</div>
            )}
          </div>
          <div className="relative">
            <label className="block text-xs mb-1 font-semibold">Expires</label>
            <input
              type="date"
              onChange={handleChange}
              required
              value={
                form.expiresAt
                  ? format(new Date(form.expiresAt), "yyyy-MM-dd")
                  : ""
              }
              className="custom-select"
              name="expiresAt"
              placeholder="Notification expiration"
              min={new Date().toISOString().split("T")[0]}
            />
          </div>
        </div>
        {form.entityType === "LINK" && (
          <div className="w-full">
            <label className="block text-xs mb-1 font-semibold">Link</label>
            <input
              type="text"
              value={form.link}
              onChange={handleChange}
              name="link"
              className={`w-full border p-2 text-xs ${errors.link && "border-red-500"}`}
              placeholder="Notification link"
              required={form.entityType === "LINK"}
              disabled={form.entityType !== "LINK"}
            />
            {errors.link && (
              <div className="text-red-600 text-xs">{errors.link}</div>
            )}
          </div>
        )}
      </div>
      <ActionButtons onCancel={onCancel} isNew={isNew} />
    </form>
  );
};

const ActionButtons = ({ onCancel, isNew }) => {
  return (
    <div className="flex flex-col gap-6 mt-4">
      <hr />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-6 py-2 border text-gray-700 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="text-sm px-6 py-2 bg-lime-500 text-white hover:bg-lime-600"
        >
          {isNew ? "Send" : "Update"}
        </button>
      </div>
    </div>
  );
};

export default PushNotificationForm;
